import React, { memo } from 'react';

import type { ControllerRenderProps, FieldValues } from 'react-hook-form';

import type { RAsyncItemsRendererProps, ShowMoreButtonRendererProps } from '@/_components';
import { Button, FormControl, FormField, FormItem, FormMessage, RAsyncMultiCombobox } from '@/_components';

import { QuickAddContainer } from './quick-add-container';
import { SortableItemsList } from './sortable-items-list';
import type { QuickAddItemBase, QuickAddSectionProps } from './types';

import { NoDataMessage } from '../no-data-message';

function AsyncQuickAddSectionBase<T extends QuickAddItemBase, TFormValues extends FieldValues = FieldValues>({
  items,
  sectionTitle,
  title,
  icon = '/images/icons/users.svg',
  emptyDataMessage,
  control,
  fieldName,
  isEditable = false,
  renderItem,
  rightSlot,
  sortable = false,
  onValuesChange,
  ...asyncMultiProps
}: QuickAddSectionProps<T, TFormValues>) {
  const renderSelectedQuickAdd: RAsyncItemsRendererProps<T> = React.useCallback(
    ({ values, removeItem }) => (
      <div className="flex flex-col">
        {values.length > 0 ? (
          values.map((item, index) => {
            if (!item) {
              console.error('Contact not matched in the map', item);
              return null;
            }
            return renderItem(item, { index, isEditable, removeItem });
          })
        ) : (
          <NoDataMessage message={emptyDataMessage} />
        )}
      </div>
    ),
    [isEditable, emptyDataMessage, renderItem]
  );

  const renderShowMoreButton: ShowMoreButtonRendererProps = React.useCallback(
    ({ onClick, text }) => (
      <Button size="sm" as="link" className="dark:text-primary mt-2.5" block onClick={onClick}>
        {text}
      </Button>
    ),
    []
  );

  return (
    <QuickAddContainer sectionTitle={sectionTitle} icon={icon} rightSlot={rightSlot}>
      <FormField
        control={control}
        name={fieldName}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <RAsyncMultiCombobox<T>
                label={title}
                options={items}
                value={items.length > 0 ? items[0] : [{ id: -1 } as T]}
                onValueChange={onValuesChange ?? field.onChange}
                isEditable={isEditable}
                paginatedItems={5}
                hideClearIndicator
                renderItems={
                  sortable
                    ? ({ values, removeItem }) => (
                        <SortableItemsList
                          field={field as ControllerRenderProps}
                          values={values}
                          isEditable={isEditable}
                          emptyDataMessage={emptyDataMessage}
                          removeItem={removeItem}
                          renderItem={renderItem}
                        />
                      )
                    : renderSelectedQuickAdd
                }
                renderShowMoreButton={renderShowMoreButton}
                {...asyncMultiProps}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </QuickAddContainer>
  );
}

export const AsyncQuickAddSection = memo(AsyncQuickAddSectionBase) as typeof AsyncQuickAddSectionBase;
